export type CategoryFlat = {
  id: string;
  name: string;
  parent_id: string | null;
  /** Present when loaded from API list. */
  product_count?: number;
};

export type CategoryTreeNode<T extends { id: string; name: string; parent_id: string | null } = CategoryFlat> = T & {
  children: CategoryTreeNode<T>[];
};

export function buildCategoryTree<T extends { id: string; name: string; parent_id: string | null }>(
  list: T[],
): CategoryTreeNode<T>[] {
  const byId = new Map<string, CategoryTreeNode<T>>();

  for (const item of list) {
    byId.set(item.id, { ...item, children: [] });
  }

  const roots: CategoryTreeNode<T>[] = [];

  for (const node of byId.values()) {
    const parentId = node.parent_id;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortRec = (nodes: CategoryTreeNode<T>[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) {
      if (n.children.length) sortRec(n.children);
    }
  };

  sortRec(roots);

  return roots;
}

/** Keep tree shape but only branches that match search or have matching descendants. */
export function filterCategoryTree<T extends { id: string; name: string; parent_id: string | null }>(
  nodes: CategoryTreeNode<T>[],
  term: string,
): CategoryTreeNode<T>[] {
  const qq = term.trim().toLowerCase();
  if (!qq) return nodes;

  const walk = (list: CategoryTreeNode<T>[]): CategoryTreeNode<T>[] => {
    const result: CategoryTreeNode<T>[] = [];
    for (const node of list) {
      const children = walk(node.children);
      const matches = node.name.toLowerCase().includes(qq);
      if (matches || children.length) {
        result.push({ ...node, children });
      }
    }
    return result;
  };

  return walk(nodes);
}

export function collectCategoryTreeIds<T extends { id: string }>(nodes: CategoryTreeNode<T>[]): string[] {
  const ids: string[] = [];
  const walk = (list: CategoryTreeNode<T>[]) => {
    for (const n of list) {
      ids.push(n.id);
      if (n.children.length) walk(n.children);
    }
  };
  walk(nodes);
  return ids;
}

/** DFS list of visible rows respecting expand/collapse. */
export function flattenVisibleCategoryRows<T extends { id: string; name: string; parent_id: string | null }>(
  nodes: CategoryTreeNode<T>[],
  expandedIds: Set<string>,
): Array<{ node: CategoryTreeNode<T>; depth: number }> {
  const out: Array<{ node: CategoryTreeNode<T>; depth: number }> = [];
  const walk = (list: CategoryTreeNode<T>[], depth: number) => {
    for (const n of list) {
      out.push({ node: n, depth });
      if (n.children.length > 0 && expandedIds.has(n.id)) {
        walk(n.children, depth + 1);
      }
    }
  };
  walk(nodes, 0);
  return out;
}

